import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Edit, Eye, MapPin, Calculator, Save, ChevronLeft, Trash2, Printer } from 'lucide-react';
import { supabase } from './supabaseClient';

// Import the PDF View component
const EstimatePDFView = ({ estimate, customer, estimateItems, onClose }) => {
const handlePrint = () => {
    // Create a new window for printing to avoid sidebar issues
    const printWindow = window.open('', '_blank');
    const printContent = document.querySelector('.estimate-print-content');
    
    // Clone the content and remove the on-screen header
    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = printContent.innerHTML;
    
    // Remove the on-screen header (the one with class "text-center mb-4 border-b border-gray-300 pb-3")
    const headerToRemove = contentDiv.querySelector('.text-center.mb-4.border-b.border-gray-300.pb-3');
    if (headerToRemove) {
      headerToRemove.remove();
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Estimate ${estimate.estimate_number}</title>
          <style>
            body {
              font-family: system-ui, -apple-system, sans-serif;
              margin: 0;
              padding: 16px;
              line-height: 1.3;
            }
            .estimate-header { 
              text-align: center; 
              margin-bottom: 1.5rem; 
              border-bottom: 2px solid #991b1b; 
              padding-bottom: 0.75rem; 
            }
            .company-logo { 
              height: 3.5rem; 
              max-width: 14rem; 
              margin: 0 auto 0.5rem; 
              display: block; 
            }
            .logo-fallback { 
              height: 3.5rem; 
              width: 12rem; 
              background: #991b1b; 
              color: white; 
              border-radius: 4px; 
              display: none; 
              align-items: center; 
              justify-content: center; 
              margin: 0 auto 0.5rem; 
              font-weight: 700; 
              font-size: 1.25rem; 
              letter-spacing: 0.05em;
            }
            .company-info { 
              color: #4b5563; 
              font-size: 0.75rem; 
              margin-top: 0.5rem; 
            }
            .company-info p { 
              margin: 0.1rem 0; 
            }
            .grid { display: grid; }
            .grid-cols-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            .gap-6 { gap: 1.5rem; }
            .mb-4 { margin-bottom: 0.75rem; }
            .mb-3 { margin-bottom: 0.5rem; }
            .mb-2 { margin-bottom: 0.35rem; }
            .mb-0\\.5 { margin-bottom: 0.125rem; }
            .p-3 { padding: 0.5rem; }
            .bg-gray-50 { background-color: #f9fafb; }
            .bg-blue-50 { background-color: #eff6ff; }
            .bg-yellow-50 { background-color: #fffbeb; }
            .rounded-lg { border-radius: 0.375rem; }
            .border { border: 1px solid #d1d5db; }
            .border-l-4 { border-left: 3px solid; }
            .border-yellow-400 { border-left-color: #f59e0b; }
            .border-t { border-top: 1px solid #d1d5db; }
            .border-gray-300 { border-color: #d1d5db; }
            .border-gray-200 { border-color: #e5e7eb; }
            .font-bold { font-weight: 700; }
            .font-semibold { font-weight: 600; }
            .font-medium { font-weight: 500; }
            .text-2xl { font-size: 1.25rem; }
            .text-sm { font-size: 0.8125rem; }
            .text-xs { font-size: 0.75rem; }
            .text-gray-800 { color: #1f2937; }
            .text-gray-700 { color: #374151; }
            .text-gray-600 { color: #4b5563; }
            .text-gray-500 { color: #6b7280; }
            .text-center { text-align: center; }
            .text-right { text-align: right; }
            .text-left { text-align: left; }
            .space-y-1 > * + * { margin-top: 0.25rem; }
            .space-y-0\\.5 > * + * { margin-top: 0.125rem; }
            .flex { display: flex; }
            .justify-between { justify-content: space-between; }
            .justify-end { justify-content: flex-end; }
            .items-center { align-items: center; }
            .w-28 { width: 7rem; }
            .w-64 { width: 16rem; }
            .w-16 { width: 4rem; }
            .w-20 { width: 5rem; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 0.5rem 0.75rem; text-align: left; }
            th { background-color: #f9fafb; font-weight: 600; color: #374151; font-size: 0.75rem; }
            .text-center th, .text-center td { text-align: center; }
            .text-right th, .text-right td { text-align: right; }
            .border-t-row { border-top: 1px solid #e5e7eb; }
            .pt-3 { padding-top: 0.5rem; }
            .pt-2 { padding-top: 0.35rem; }
            .pt-1 { padding-top: 0.25rem; }
            .mt-4 { margin-top: 0.75rem; }
            .mt-3 { margin-top: 0.5rem; }
            .mt-2 { margin-top: 0.35rem; }
            .mt-1\\.5 { margin-top: 0.375rem; }
            .mt-1 { margin-top: 0.25rem; }
            .h-10 { height: 2.5rem; }
            .h-6 { height: 1.5rem; }
            .px-3 { padding-left: 0.75rem; padding-right: 0.75rem; }
            .py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
            .py-4 { padding-top: 1rem; padding-bottom: 1rem; }
            .max-w-40 { max-width: 10rem; }
            @page { margin: 0.5in; }
            @media print {
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="estimate-header">
            <img 
              src="${window.location.origin}/jft-logo.png" 
              alt="JFT Contracting" 
              class="company-logo" 
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';"
            />
            <div class="logo-fallback">JFT CONTRACTING</div>
            <div class="company-info">
              <p class="font-semibold">Professional Contracting Services</p>
              <p>Thunder Bay, Ontario • (807) 555-0123 • info@jftcontracting.com</p>
            </div>
          </div>
          ${contentDiv.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };


  const handleEmail = () => {
    const subject = `Estimate ${estimate.estimate_number} - JFT Contracting`;
    const body = `Dear ${customer?.first_name} ${customer?.last_name},

Please find attached your estimate ${estimate.estimate_number} for the work at ${estimate.job_address}.

If you have any questions, please don't hesitate to contact us.

Best regards,
JFT Contracting Team`;

    const mailtoLink = `mailto:${customer?.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-auto">
        {/* Header Controls */}
        <div className="flex justify-between items-center p-4 border-b bg-gray-50">
          <h2 className="text-xl font-bold text-gray-800">Estimate Preview</h2>
          <div className="flex space-x-2">
            <button
              onClick={handlePrint}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Printer size={16} />
              <span>Print</span>
            </button>
            {customer?.email && (
              <button
                onClick={handleEmail}
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <span>âœ‰</span>
                <span>Email</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <span>Ã—</span>
              <span>Close</span>
            </button>
          </div>
        </div>

        {/* Estimate Document */}
        <div className="estimate-print-content p-6 bg-white" style={{ maxHeight: '11in' }}>
          {/* Company Header */}
          <div className="text-center mb-4 border-b border-gray-300 pb-3">
            <div className="flex justify-center mb-2">
              <img
                src="/jft-logo.png"
                alt="JFT Contracting"
                className="h-10 w-auto max-w-40"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="h-10 w-32 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-600" style={{display: 'none'}}>
                JFT Contracting
              </div>
            </div>
            <div className="text-gray-600 text-xs">
              <p className="font-semibold mb-0.5">Professional Contracting Services</p>
              <p>Thunder Bay, Ontario â€¢ (807) 555-0123 â€¢ info@jftcontracting.com</p>
            </div>
          </div>

          {/* Estimate Header */}
          <div className="grid grid-cols-2 gap-6 mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 mb-3">ESTIMATE</h1>
              <div className="space-y-1">
                <div className="flex">
                  <span className="font-semibold w-28 text-sm">Estimate #:</span>
                  <span className="text-sm">{estimate.estimate_number}</span>
                </div>
                <div className="flex">
                  <span className="font-semibold w-28 text-sm">Date:</span>
                  <span className="text-sm">{new Date(estimate.estimate_date).toLocaleDateString()}</span>
                </div>
                {estimate.valid_until && (
                  <div className="flex">
                    <span className="font-semibold w-28 text-sm">Valid Until:</span>
                    <span className="text-sm">{new Date(estimate.valid_until).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div>
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Bill To:</h3>
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <div className="font-semibold text-gray-800">
                  {customer?.first_name} {customer?.last_name}
                </div>
                {customer?.company && (
                  <div className="text-gray-600 text-xs">{customer.company}</div>
                )}
                <div className="text-gray-600 mt-1.5 text-xs">
                  <div>{customer?.address}</div>
                  <div>{customer?.city}, {customer?.province} {customer?.postal_code}</div>
                </div>
                <div className="text-gray-600 mt-1.5 text-xs">
                  <div>Phone: {customer?.phone}</div>
                  {customer?.email && <div>Email: {customer.email}</div>}
                </div>
              </div>
            </div>
          </div>

          {/* Job Location */}
          {estimate.job_address && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Job Location:</h3>
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <div>{estimate.job_address}</div>
                <div>{estimate.job_city}, {estimate.job_province} {estimate.job_postal_code}</div>
              </div>
            </div>
          )}

          {/* Services Table */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Services:</h3>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700 text-xs">Description</th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700 w-16 text-xs">Qty</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 w-20 text-xs">Rate</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-700 w-20 text-xs">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {estimateItems && estimateItems.length > 0 ? (
                    estimateItems.map((item, index) => (
                      <tr key={item.id || index} className="border-t border-gray-200">
                        <td className="px-3 py-2 text-gray-800 text-sm">{item.description}</td>
                        <td className="px-3 py-2 text-center text-gray-600 text-sm">{item.quantity}</td>
                        <td className="px-3 py-2 text-right text-gray-600 text-sm">
                          ${parseFloat(item.unit_price || 0).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-gray-800 text-sm">
                          ${parseFloat(item.line_total || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="border-t border-gray-200">
                      <td colSpan="4" className="px-3 py-4 text-center text-gray-500 text-sm">
                        No services listed
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div className="flex justify-end mb-4">
            <div className="w-64">
              <div className="bg-gray-50 p-3 rounded-lg">
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Subtotal:</span>
                    <span className="font-medium">${parseFloat(estimate.subtotal || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">HST ({estimate.hst_rate || 13}%):</span>
                    <span className="font-medium">${parseFloat(estimate.hst_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="border-t border-gray-300 pt-1 mt-1">
                    <div className="flex justify-between font-bold">
                      <span>Total:</span>
                      <span>${parseFloat(estimate.total_amount || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          {estimate.notes && (
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Notes:</h3>
              <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                <p className="text-gray-700 whitespace-pre-wrap text-xs">{estimate.notes}</p>
              </div>
            </div>
          )}

          {/* Terms and Conditions */}
          <div className="border-t border-gray-300 pt-3 mt-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Terms & Conditions:</h3>
            <div className="text-xs text-gray-600 space-y-0.5">
              <p>â€¢ This estimate is valid for 30 days from the date above.</p>
              <p>â€¢ Work will commence upon signed acceptance of this estimate and receipt of required deposit.</p>
              <p>â€¢ Payment terms: 50% deposit required, balance due upon completion.</p>
              <p>â€¢ All work performed according to industry standards and local building codes.</p>
              <p>â€¢ Weather conditions may affect scheduled work dates.</p>
              <p>â€¢ Additional work not covered in this estimate requires separate approval.</p>
            </div>
          </div>

          {/* Acceptance Section */}
          <div className="mt-4 border-t border-gray-300 pt-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">Acceptance:</h3>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs text-gray-600 mb-4">
                  I accept the above estimate and authorize JFT Contracting to proceed with the work as described.
                </p>
                <div className="border-b border-gray-400 mb-1 h-6"></div>
                <p className="text-xs text-gray-600">Customer Signature</p>
              </div>
              <div>
                <div className="mb-4"></div>
                <div className="border-b border-gray-400 mb-1 h-6"></div>
                <p className="text-xs text-gray-600">Date</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center text-xs text-gray-500 border-t border-gray-200 pt-2">
            <p>JFT Contracting â€¢ Thunder Bay, Ontario â€¢ Licensed & Insured</p>
            <p>Thank you for choosing JFT Contracting for your project needs!</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Estimate Form Component
const EstimateForm = ({ 
  formData, 
  onFormDataChange, 
  onSave, 
  onCancel, 
  saving, 
  isEditing,
  customers,
  services 
}) => {
  const [localFormData, setLocalFormData] = useState(formData);
  const [estimateItems, setEstimateItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    setLocalFormData(formData);
    setEstimateItems(formData.items || []);
    if (formData.customer_id) {
      const customer = customers.find(c => c.id === formData.customer_id);
      setSelectedCustomer(customer);
    }
  }, [formData, customers]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    const newData = { ...localFormData, [name]: value };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  const handleCustomerSelect = (customerId) => {
    const customer = customers.find(c => c.id === parseInt(customerId));
    setSelectedCustomer(customer);
    
    const newData = {
      ...localFormData,
      customer_id: parseInt(customerId),
      job_address: customer?.address || '',
      job_city: customer?.city || 'Thunder Bay',
      job_province: customer?.province || 'ON',
      job_postal_code: customer?.postal_code || ''
    };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  const addEstimateItem = () => {
    const newItem = {
      id: Date.now(), // Temporary ID for new items
      service_id: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      line_total: 0,
      sort_order: estimateItems.length
    };
    const newItems = [...estimateItems, newItem];
    setEstimateItems(newItems);
    updateTotals(newItems);
  };

  const updateEstimateItem = (index, field, value) => {
    const newItems = [...estimateItems];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // Auto-calculate line total when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const unitPrice = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].line_total = quantity * unitPrice;
    }
    
    // Auto-fill description when service is selected
    if (field === 'service_id' && value) {
      const service = services.find(s => s.id === parseInt(value));
      if (service) {
        newItems[index].description = service.service_name;
      }
    }
    
    setEstimateItems(newItems);
    updateTotals(newItems);
  };

  const removeEstimateItem = (index) => {
    const newItems = estimateItems.filter((_, i) => i !== index);
    setEstimateItems(newItems);
    updateTotals(newItems);
  };

  const updateTotals = (items) => {
    const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
    const hstRate = parseFloat(localFormData.hst_rate) || 13;
    const hstAmount = subtotal * (hstRate / 100);
    const totalAmount = subtotal + hstAmount;

    const newData = {
      ...localFormData,
      subtotal: subtotal.toFixed(2),
      hst_amount: hstAmount.toFixed(2),
      total_amount: totalAmount.toFixed(2),
      items: items
    };
    setLocalFormData(newData);
    onFormDataChange(newData);
  };

  const handleSave = () => {
    const dataToSave = {
      ...localFormData,
      items: estimateItems
    };
    onSave(dataToSave);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className={`${isEditing ? 'bg-green-500' : 'bg-blue-500'} text-white p-6 rounded-t-xl`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <FileText size={32} />
            <h2 className="text-2xl font-bold">
              {isEditing ? 'Edit Estimate' : 'New Estimate'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <ChevronLeft size={20} />
            <span>Back</span>
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Estimate Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimate Number
            </label>
            <input
              type="text"
              name="estimate_number"
              value={localFormData.estimate_number || ''}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Auto-generated"
              readOnly={!isEditing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Estimate Date *
            </label>
            <input
              type="date"
              name="estimate_date"
              value={localFormData.estimate_date || new Date().toISOString().split('T')[0]}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Valid Until
            </label>
            <input
              type="date"
              name="valid_until"
              value={localFormData.valid_until || ''}
              onChange={handleFormChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Customer Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Customer *
          </label>
          <select
            value={localFormData.customer_id || ''}
            onChange={(e) => handleCustomerSelect(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select a customer</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.first_name} {customer.last_name} {customer.company ? `(${customer.company})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Customer Info Display */}
        {selectedCustomer && (
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium text-gray-800 mb-2">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Name:</span> {selectedCustomer.first_name} {selectedCustomer.last_name}
              </div>
              <div>
                <span className="text-gray-600">Phone:</span> {selectedCustomer.phone}
              </div>
              <div>
                <span className="text-gray-600">Email:</span> {selectedCustomer.email || 'N/A'}
              </div>
              <div>
                <span className="text-gray-600">Address:</span> {selectedCustomer.address}, {selectedCustomer.city}
              </div>
            </div>
          </div>
        )}

        {/* Job Location */}
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Location</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Address
              </label>
              <input
                type="text"
                name="job_address"
                value={localFormData.job_address || ''}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Job site address"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                name="job_city"
                value={localFormData.job_city || 'Thunder Bay'}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                name="job_postal_code"
                value={localFormData.job_postal_code || ''}
                onChange={handleFormChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="P7A 1B2"
              />
            </div>
          </div>
        </div>

        {/* Estimate Items */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Services</h3>
            <button
              type="button"
              onClick={addEstimateItem}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus size={16} />
              <span>Add Service</span>
            </button>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 grid grid-cols-12 gap-2 text-sm font-medium text-gray-700">
              <div className="col-span-4">Description</div>
              <div className="col-span-2">Service</div>
              <div className="col-span-2">Quantity</div>
              <div className="col-span-2">Unit Price</div>
              <div className="col-span-2">Total</div>
            </div>

            {estimateItems.map((item, index) => (
              <div key={item.id || index} className="px-4 py-3 border-t grid grid-cols-12 gap-2 items-center">
                <div className="col-span-4">
                  <input
                    type="text"
                    value={item.description || ''}
                    onChange={(e) => updateEstimateItem(index, 'description', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="Service description"
                  />
                </div>
                <div className="col-span-2">
                  <select
                    value={item.service_id || ''}
                    onChange={(e) => updateEstimateItem(index, 'service_id', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Select service</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.service_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.quantity || ''}
                    onChange={(e) => updateEstimateItem(index, 'quantity', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="1"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="col-span-2">
                  <input
                    type="number"
                    value={item.unit_price || ''}
                    onChange={(e) => updateEstimateItem(index, 'unit_price', e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                </div>
                <div className="col-span-1">
                  <span className="text-sm font-medium">
                    ${(parseFloat(item.line_total) || 0).toFixed(2)}
                  </span>
                </div>
                <div className="col-span-1">
                  <button
                    type="button"
                    onClick={() => removeEstimateItem(index)}
                    className="text-red-500 hover:text-red-700 p-1"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {estimateItems.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                No services added yet. Click "Add Service" to get started.
              </div>
            )}
          </div>
        </div>

        {/* Totals */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div></div>
            <div></div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span className="font-medium">${(parseFloat(localFormData.subtotal) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span>HST ({localFormData.hst_rate || 13}%):</span>
                <span className="font-medium">${(parseFloat(localFormData.hst_amount) || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>${(parseFloat(localFormData.total_amount) || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Notes
          </label>
          <textarea
            name="notes"
            value={localFormData.notes || ''}
            onChange={handleFormChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Additional notes for this estimate..."
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
              saving
                ? 'bg-gray-400 cursor-not-allowed'
                : isEditing
                ? 'bg-green-500 hover:bg-green-600'
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white`}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>{isEditing ? 'Update Estimate' : 'Save Estimate'}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Estimation System Component
function EstimationSystem({ onConvertToJob }) {
  const [activeView, setActiveView] = useState('list');
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [estimates, setEstimates] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPDFView, setShowPDFView] = useState(false);
  const [pdfEstimate, setPdfEstimate] = useState(null);
  const [pdfCustomer, setPdfCustomer] = useState(null);
  const [pdfItems, setPdfItems] = useState([]);
  const [formData, setFormData] = useState({
    estimate_number: '',
    estimate_date: new Date().toISOString().split('T')[0],
    customer_id: null,
    job_address: '',
    job_city: 'Thunder Bay',
    job_province: 'ON',
    job_postal_code: '',
    subtotal: 0,
    hst_rate: 13,
    hst_amount: 0,
    total_amount: 0,
    status: 'Draft',
    notes: '',
    valid_until: '',
    items: []
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load estimates with customer data
      const { data: estimatesData, error: estimatesError } = await supabase
        .from('estimates')
        .select(`
          *,
          customer:customers (
            id,
            first_name,
            last_name,
            company,
            phone
          )
        `)
        .order('created_at', { ascending: false });

      if (estimatesError) throw estimatesError;

      // Load customers
      const { data: customersData, error: customersError } = await supabase
        .from('customers')
        .select('*')
        .eq('status', 'Active')
        .order('first_name');

      if (customersError) throw customersError;

      // Load services
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*')
        .eq('status', 'Active')
        .order('sort_order', { ascending: true, nullsLast: true })
        .order('service_name');

      if (servicesError) throw servicesError;

      setEstimates(estimatesData || []);
      setCustomers(customersData || []);
      setServices(servicesData || []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error loading data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      estimate_number: '',
      estimate_date: new Date().toISOString().split('T')[0],
      customer_id: null,
      job_address: '',
      job_city: 'Thunder Bay',
      job_province: 'ON',
      job_postal_code: '',
      subtotal: 0,
      hst_rate: 13,
      hst_amount: 0,
      total_amount: 0,
      status: 'Draft',
      notes: '',
      valid_until: '',
      items: []
    });
  };

  const handleFormDataChange = (newData) => {
    setFormData(newData);
  };

  const handleSave = async (dataToSave) => {
    if (!dataToSave.customer_id) {
      alert('Please select a customer');
      return;
    }

    if (!dataToSave.items || dataToSave.items.length === 0) {
      alert('Please add at least one service');
      return;
    }

    try {
      setSaving(true);

      if (activeView === 'edit' && selectedEstimate) {
        // Update existing estimate
        const { items, customer, ...estimateData } = dataToSave;
        
        const { error: estimateError } = await supabase
          .from('estimates')
          .update(estimateData)
          .eq('id', selectedEstimate.id);

        if (estimateError) throw estimateError;

        // Delete existing items and insert new ones
        const { error: deleteError } = await supabase
          .from('estimate_items')
          .delete()
          .eq('estimate_id', selectedEstimate.id);

        if (deleteError) throw deleteError;

        if (items && items.length > 0) {
          const itemsToInsert = items.map(item => ({
            estimate_id: selectedEstimate.id,
            service_id: item.service_id || null,
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            line_total: parseFloat(item.line_total) || 0,
            sort_order: item.sort_order || 0
          }));

          const { error: itemsError } = await supabase
            .from('estimate_items')
            .insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }

        alert('Estimate updated successfully!');
      } else {
        // Create new estimate
        const { items, customer, ...estimateData } = dataToSave;

        // Generate estimate number if not provided
        if (!estimateData.estimate_number) {
          // Generate estimate number in JavaScript
          const year = new Date().getFullYear();
          const { data: existingEstimates, error: fetchError } = await supabase
            .from('estimates')
            .select('estimate_number')
            .like('estimate_number', `EST-${year}-%`);
          
          if (fetchError) throw fetchError;
          
          const existingNumbers = (existingEstimates || [])
            .map(e => {
              const parts = e.estimate_number.split('-');
              return parts.length === 3 ? parseInt(parts[2]) : 0;
            })
            .filter(n => !isNaN(n));
          
          const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
          estimateData.estimate_number = `EST-${year}-${nextNumber.toString().padStart(3, '0')}`;
        }

        const { data: newEstimate, error: estimateError } = await supabase
          .from('estimates')
          .insert([estimateData])
          .select()
          .single();

        if (estimateError) throw estimateError;

        // Insert estimate items
        if (items && items.length > 0) {
          const itemsToInsert = items.map(item => ({
            estimate_id: newEstimate.id,
            service_id: item.service_id || null,
            description: item.description,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            line_total: parseFloat(item.line_total) || 0,
            sort_order: item.sort_order || 0
          }));

          const { error: itemsError } = await supabase
            .from('estimate_items')
            .insert(itemsToInsert);

          if (itemsError) throw itemsError;
        }

        alert('Estimate created successfully!');
      }

      await loadData();
      setActiveView('list');
      resetForm();

    } catch (error) {
      console.error('Error saving estimate:', error);
      alert('Error saving estimate: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setActiveView('list');
    resetForm();
  };

  const showEstimatePDF = async (estimate) => {
    try {
      // Load customer details
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', estimate.customer_id)
        .single();

      if (customerError) throw customerError;

      // Load estimate items
      const { data: itemsData, error: itemsError } = await supabase
        .from('estimate_items')
        .select('*')
        .eq('estimate_id', estimate.id)
        .order('sort_order');

      if (itemsError) throw itemsError;

      setPdfEstimate(estimate);
      setPdfCustomer(customerData);
      setPdfItems(itemsData || []);
      setShowPDFView(true);
    } catch (error) {
      console.error('Error loading estimate details:', error);
      alert('Error loading estimate details');
    }
  };

  const handleConvertToJob = async (estimate) => {
    if (!onConvertToJob) return;

    try {
      // Load customer details
      const { data: customerData, error: customerError } = await supabase
        .from('customers')
        .select('*')
        .eq('id', estimate.customer_id)
        .single();

      if (customerError) throw customerError;

      // Load estimate items
      const { data: itemsData, error: itemsError } = await supabase
        .from('estimate_items')
        .select('*')
        .eq('estimate_id', estimate.id)
        .order('sort_order');

      if (itemsError) throw itemsError;

      // Prepare conversion data
      const conversionData = {
        estimate_id: estimate.id,
        estimate_number: estimate.estimate_number,
        customer_id: estimate.customer_id,
        customer: customerData,
        job_address: estimate.job_address,
        job_city: estimate.job_city,
        job_province: estimate.job_province,
        job_postal_code: estimate.job_postal_code,
        total_amount: estimate.total_amount,
        estimate_items: itemsData || [],
        notes: `Converted from estimate ${estimate.estimate_number}\n\n${estimate.notes || ''}`
      };

      onConvertToJob(conversionData);
    } catch (error) {
      console.error('Error converting estimate to job:', error);
      alert('Error converting estimate to job: ' + error.message);
    }
  };

  const updateEstimateStatus = async (estimateId, newStatus) => {
    try {
      const { error } = await supabase
        .from('estimates')
        .update({ status: newStatus })
        .eq('id', estimateId);

      if (error) throw error;

      // Update local state
      setEstimates(estimates.map(est =>
        est.id === estimateId ? { ...est, status: newStatus } : est
      ));

      if (selectedEstimate && selectedEstimate.id === estimateId) {
        setSelectedEstimate({ ...selectedEstimate, status: newStatus });
      }

      alert(`Estimate status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating estimate status:', error);
      alert('Error updating estimate status: ' + error.message);
    }
  };

  // Filter estimates
  const filteredEstimates = estimates.filter(estimate => {
    const customer = estimate.customer;
    const customerName = customer ? `${customer.first_name} ${customer.last_name} ${customer.company || ''}` : '';
    
    const matchesSearch = 
      estimate.estimate_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      estimate.job_address?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = !statusFilter || estimate.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Show form for new or edit
  if (activeView === 'new' || activeView === 'edit') {
    return (
      <EstimateForm
        formData={formData}
        onFormDataChange={handleFormDataChange}
        onSave={handleSave}
        onCancel={handleCancel}
        saving={saving}
        isEditing={activeView === 'edit'}
        customers={customers}
        services={services}
      />
    );
  }

  // Estimate Detail View
  if (activeView === 'detail') {
    return (
      <div className="bg-white rounded-xl shadow-lg">
        {/* Header */}
        <div className="bg-green-500 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <FileText size={32} />
              <div>
                <h2 className="text-2xl font-bold">{selectedEstimate?.estimate_number}</h2>
                <p className="text-green-100">
                  {selectedEstimate?.customer ? 
                    `${selectedEstimate.customer.first_name} ${selectedEstimate.customer.last_name}` : 
                    'No customer'
                  }
                </p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={async () => {
                  // Load estimate with items for editing
                  try {
                    const { data: items, error } = await supabase
                      .from('estimate_items')
                      .select('*')
                      .eq('estimate_id', selectedEstimate.id)
                      .order('sort_order');
                    
                    if (error) throw error;
                    
                    setFormData({
                      ...selectedEstimate,
                      items: items || []
                    });
                    setActiveView('edit');
                  } catch (error) {
                    console.error('Error loading estimate items:', error);
                    alert('Error loading estimate details');
                  }
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <Edit size={20} />
                <span>Edit</span>
              </button>
              <button
                onClick={() => setActiveView('list')}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
              >
                <ChevronLeft size={20} />
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Estimate Details */}
        <div className="p-6">
          {/* Status Change Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">Change Estimate Status:</h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => updateEstimateStatus(selectedEstimate.id, 'Draft')}
                disabled={selectedEstimate?.status === 'Draft'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedEstimate?.status === 'Draft'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-yellow-500 hover:bg-yellow-600 text-white'
                }`}
              >
                Draft
              </button>
              <button
                onClick={() => updateEstimateStatus(selectedEstimate.id, 'Sent')}
                disabled={selectedEstimate?.status === 'Sent'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedEstimate?.status === 'Sent'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                Sent
              </button>
              <button
                onClick={() => updateEstimateStatus(selectedEstimate.id, 'Accepted')}
                disabled={selectedEstimate?.status === 'Accepted'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedEstimate?.status === 'Accepted'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                ✓ Accepted
              </button>
              <button
                onClick={() => updateEstimateStatus(selectedEstimate.id, 'Declined')}
                disabled={selectedEstimate?.status === 'Declined'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedEstimate?.status === 'Declined'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                ✗ Declined
              </button>
              <button
                onClick={() => updateEstimateStatus(selectedEstimate.id, 'Expired')}
                disabled={selectedEstimate?.status === 'Expired'}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedEstimate?.status === 'Expired'
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              >
                Expired
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-2">Current status: <span className="font-semibold">{selectedEstimate?.status}</span></p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Estimate Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Estimate Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Estimate Number:</span>
                  <span className="font-medium">{selectedEstimate?.estimate_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">{new Date(selectedEstimate?.estimate_date).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`font-medium ${
                    selectedEstimate?.status === 'Accepted' ? 'text-green-600' :
                    selectedEstimate?.status === 'Sent' ? 'text-blue-600' :
                    selectedEstimate?.status === 'Declined' ? 'text-red-600' :
                    'text-yellow-600'
                  }`}>
                    {selectedEstimate?.status}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Amount:</span>
                  <span className="font-bold text-lg">${selectedEstimate?.total_amount}</span>
                </div>
              </div>
            </div>

            {/* Job Location */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Job Location</h3>
              <div className="space-y-2">
                <p>{selectedEstimate?.job_address}</p>
                <p>{selectedEstimate?.job_city}, {selectedEstimate?.job_province} {selectedEstimate?.job_postal_code}</p>
              </div>
            </div>
          </div>

          {selectedEstimate?.notes && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Notes</h3>
              <p className="text-gray-600 bg-gray-50 p-4 rounded-lg">{selectedEstimate.notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Generate estimate number for new estimates
  const generateEstimateNumber = () => {
    const year = new Date().getFullYear();
    const existingNumbers = estimates
      .filter(e => e.estimate_number?.startsWith(`EST-${year}-`))
      .map(e => {
        const parts = e.estimate_number.split('-');
        return parts.length === 3 ? parseInt(parts[2]) : 0;
      })
      .filter(n => !isNaN(n));
    
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `EST-${year}-${nextNumber.toString().padStart(3, '0')}`;
  };

  // Estimates List View (Default)
  return (
    <>
      {/* PDF View Modal */}
      {showPDFView && (
        <EstimatePDFView
          estimate={pdfEstimate}
          customer={pdfCustomer}
          estimateItems={pdfItems}
          onClose={() => setShowPDFView(false)}
        />
      )}

      <div className="bg-white rounded-xl shadow-lg">
        {/* Header */}
        <div className="bg-green-500 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <FileText size={32} />
              <div>
                <h2 className="text-2xl font-bold">Estimation System</h2>
                <p className="text-green-100">{estimates.length} estimates</p>
              </div>
            </div>
            <button
              onClick={() => {
                resetForm();
                setFormData(prev => ({
                  ...prev,
                  estimate_number: generateEstimateNumber()
                }));
                setActiveView('new');
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Plus size={20} />
              <span>New Estimate</span>
            </button>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="p-6 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by estimate number, customer, or address..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Sent">Sent</option>
                <option value="Accepted">Accepted</option>
                <option value="Declined">Declined</option>
                <option value="Expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {/* Estimates List */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading estimates...</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEstimates.map(estimate => (
                <div key={estimate.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between gap-4">
                    <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
                      <FileText className="text-green-600" size={24} />
                    </div>

                    <div className="flex-shrink-0 w-36">
                      <h3 className="text-base font-semibold text-gray-800">{estimate.estimate_number}</h3>
                    </div>

                    <div className="flex-1 min-w-0">
                      <p className="text-base text-gray-600 truncate">
                        {estimate.customer ?
                          `${estimate.customer.first_name} ${estimate.customer.last_name}${estimate.customer.company ? ` (${estimate.customer.company})` : ''}` :
                          'No customer'
                        }
                      </p>
                    </div>

                    <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0">
                      <Calculator size={18} />
                      <span>${estimate.total_amount}</span>
                    </div>

                    <div className="text-base text-gray-600 flex-shrink-0 w-28 text-center">
                      {new Date(estimate.estimate_date).toLocaleDateString()}
                    </div>

                    <div className="flex items-center space-x-2 text-base text-gray-600 flex-shrink-0 w-32">
                      <MapPin size={18} />
                      <span className="truncate">{estimate.job_city}</span>
                    </div>

                    <div className="flex-shrink-0">
                      <span className={`px-3 py-1 rounded-full text-sm whitespace-nowrap ${
                        estimate.status === 'Accepted' ? 'bg-green-100 text-green-800' :
                        estimate.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                        estimate.status === 'Declined' ? 'bg-red-100 text-red-800' :
                        estimate.status === 'Expired' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {estimate.status}
                      </span>
                    </div>

                    <div className="flex space-x-1 flex-shrink-0">
                      <button
                        onClick={() => showEstimatePDF(estimate)}
                        className="bg-purple-500 hover:bg-purple-600 text-white p-2 rounded-lg transition-colors"
                        title="View/Print Estimate"
                      >
                        <Printer size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedEstimate(estimate);
                          setActiveView('detail');
                        }}
                        className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={async () => {
                          // Load estimate with items for editing
                          try {
                            const { data: items, error } = await supabase
                              .from('estimate_items')
                              .select('*')
                              .eq('estimate_id', estimate.id)
                              .order('sort_order');

                            if (error) throw error;

                            setFormData({
                              ...estimate,
                              items: items || []
                            });
                            setSelectedEstimate(estimate);
                            setActiveView('edit');
                          } catch (error) {
                            console.error('Error loading estimate items:', error);
                            alert('Error loading estimate details');
                          }
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                        title="Edit Estimate"
                      >
                        <Edit size={16} />
                      </button>
                      {estimate.status === 'Accepted' && onConvertToJob && (
                        <button
                          onClick={() => handleConvertToJob(estimate)}
                          className="bg-pink-500 hover:bg-pink-600 text-white px-3 py-2 rounded-lg transition-colors"
                          title="Convert to Job"
                        >
                          <span className="text-xs font-semibold">→ JOB</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && filteredEstimates.length === 0 && (
            <div className="text-center py-8">
              <FileText className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-500">
                {searchTerm || statusFilter ? 'No estimates found matching your criteria.' : 'No estimates yet. Create your first estimate!'}
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default EstimationSystem;